import React, { useState } from 'react';
import { Activity, Save, BookOpen as BookOpenIcon } from 'lucide-react';
import { useCalculator } from '../../context/CalculatorContext';
import DynamicChartSystem from './DynamicChartSystem';
import TeachingModule from '../TeachingModule';
import { teachingContent } from '../../data/teachingContent';
import { formatCurrency, shortenLargeNumber } from '../../utils/formatters';

interface CashFlowChartProps {}

const CashFlowChart: React.FC<CashFlowChartProps> = () => {
  const { 
    investment, 
    results, 
    cashFlowChartVisibility, 
    toggleCashFlowChartElement,
    resetCashFlowChartVisibility
  } = useCalculator();
  
  const [showTeachingModule, setShowTeachingModule] = useState(false);

  if (!results) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Cargando gráficos...</p>
          <p className="text-gray-500 text-sm mt-2">Procesando datos de flujo de efectivo</p>
        </div>
      </div>
    );
  }

  const yearlyData = results.yearlyData;
  
  // Prepare data for the chart - always include all data points
  const allChartData = yearlyData?.map(data => {
    const baseData: Record<string, any> = {
      year: data.year,
      'Flujo Cosecha Capital': data.availableCashFlow,
      'Flujo CETES': data.cetesCashFlow || 0,
      'Flujo Ahorro': data.savingsCashFlow || 0,
      'Flujo Bienes Raíces': data.realEstateCashFlow || 0,
      'Flujo Acumulado': data.availableCashFlow
    };
    
    // Add partial cash out if enabled
    if (investment.partialCashOut) {
      baseData['Retiro Parcial'] = data.partialCashOutAmount || 0;
    }
    
    return baseData;
  }) || [];
  
  // Define chart elements with extremely subtle appearance when toggled off
  const chartElements = [
    {
      dataKey: 'Flujo Cosecha Capital',
      name: 'Cosecha Capital',
      color: cashFlowChartVisibility.cosechaCapital ? '#16a34a' : '#e0e0e0',
      strokeWidth: cashFlowChartVisibility.cosechaCapital ? 3 : 0.5,
      fillOpacity: cashFlowChartVisibility.cosechaCapital ? 0.6 : 0.01,
      strokeDasharray: cashFlowChartVisibility.cosechaCapital ? undefined : '1 5'
    },
    {
      dataKey: 'Flujo CETES',
      name: 'CETES',
      color: cashFlowChartVisibility.cetes ? '#0284c7' : '#e0e0e0',
      strokeDasharray: cashFlowChartVisibility.cetes ? '5 5' : '1 5',
      strokeWidth: cashFlowChartVisibility.cetes ? 2 : 0.5,
      fillOpacity: cashFlowChartVisibility.cetes ? 0.4 : 0.01
    },
    {
      dataKey: 'Flujo Ahorro',
      name: 'Ahorro Tradicional',
      color: cashFlowChartVisibility.savings ? '#7c3aed' : '#e0e0e0',
      strokeDasharray: cashFlowChartVisibility.savings ? '5 5' : '1 5',
      strokeWidth: cashFlowChartVisibility.savings ? 2 : 0.5,
      fillOpacity: cashFlowChartVisibility.savings ? 0.4 : 0.01
    },
    {
      dataKey: 'Flujo Bienes Raíces',
      name: 'Bienes Raíces',
      color: cashFlowChartVisibility.realEstate ? '#ea580c' : '#e0e0e0',
      strokeDasharray: cashFlowChartVisibility.realEstate ? '5 5' : '1 5',
      strokeWidth: cashFlowChartVisibility.realEstate ? 2 : 0.5,
      fillOpacity: cashFlowChartVisibility.realEstate ? 0.4 : 0.01
    },
    {
      dataKey: 'Flujo Acumulado',
      name: 'Flujo Acumulado',
      color: cashFlowChartVisibility.cumulativeFlow ? '#64748b' : '#e0e0e0',
      strokeDasharray: '3 3',
      strokeWidth: cashFlowChartVisibility.cumulativeFlow ? 2 : 0.5,
      fillOpacity: cashFlowChartVisibility.cumulativeFlow ? 0.3 : 0.01
    },
    {
      dataKey: 'Retiro Parcial',
      name: 'Retiro Parcial',
      color: investment.partialCashOut && cashFlowChartVisibility.showPartialCashOut ? '#f59e0b' : '#e0e0e0',
      strokeWidth: investment.partialCashOut && cashFlowChartVisibility.showPartialCashOut ? 2 : 0.5,
      fillOpacity: investment.partialCashOut && cashFlowChartVisibility.showPartialCashOut ? 0.5 : 0.01,
      strokeDasharray: '3 3'
    }
  ];

  // Prepare data for pie chart (positive vs negative cash flows)
  const totalPositiveFlow = yearlyData
    .filter(data => data.availableCashFlow > 0)
    .reduce((sum, data) => sum + data.availableCashFlow, 0);
  
  const totalNegativeFlow = Math.abs(yearlyData
    .filter(data => data.availableCashFlow < 0)
    .reduce((sum, data) => sum + data.availableCashFlow, 0));

  const pieData = [
    {
      name: 'Flujos Positivos',
      value: totalPositiveFlow,
      color: cashFlowChartVisibility.cosechaCapital ? '#16a34a' : '#e0e0e0'
    },
    {
      name: 'Inversión Inicial',
      value: totalNegativeFlow,
      color: '#dc2626'
    },
    ...(investment.partialCashOut && cashFlowChartVisibility.showPartialCashOut && results.totalCashOut ? [{
      name: 'Retiro Parcial',
      value: results.totalCashOut,
      color: cashFlowChartVisibility.showPartialCashOut ? '#f59e0b' : '#e0e0e0'
    }] : [])
  ];

  // Prepare chart system configuration
  const chartCategories = [
    {
      id: 'cashflow',
      title: 'Flujo de Efectivo Anual',
      description: 'Análisis de flujo de efectivo y retorno de inversión con controles de animación',
      icon: <Activity className="w-5 h-5" />,
      color: 'from-green-500 to-green-600',
      availableTypes: ['line', 'bar', 'area', 'pie'] as const
    }
  ];

  const chartSystemData = {
    cashflow: allChartData
  };

  const chartSystemElements = {
    cashflow: chartElements
  };

  const pieChartData = {
    cashflow: pieData
  };

  // Calculate monthly partial cash out for the final year
  const finalYearData = yearlyData[yearlyData.length - 1];
  const monthlyPartialCashOut = investment.partialCashOut ? 
    (finalYearData.partialCashOutAmount || 0) / 12 : 0;

  const summaryData = {
    cashflow: [
      {
        label: 'Flujo Final',
        value: formatCurrency(yearlyData[yearlyData.length - 1]?.availableCashFlow || 0, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: 'Flujos Positivos',
        value: formatCurrency(totalPositiveFlow, investment.currencyFormat),
        color: 'text-green-600 bg-green-50'
      },
      {
        label: 'Inversión Inicial',
        value: formatCurrency(totalNegativeFlow, investment.currencyFormat),
        color: 'text-red-600 bg-red-50'
      },
      {
        label: 'Año Recuperación',
        value: results.paybackYear ? `Año ${results.paybackYear}` : 'N/A',
        color: 'text-blue-600 bg-blue-50'
      },
      ...(investment.partialCashOut ? [{
        label: 'Retiro Parcial Mensual',
        value: formatCurrency(monthlyPartialCashOut, investment.currencyFormat),
        color: 'text-amber-600 bg-amber-50'
      }] : [])
    ]
  };

  const additionalInfo = {
    cashflow: results.paybackYear ? (
      <div className="p-4 bg-green-50 rounded-lg">
        <p className="text-sm text-green-800">
          <strong>Año de Recuperación Estimado:</strong> Año {results.paybackYear}
        </p>
        <p className="text-xs text-green-600 mt-1">
          A partir de este año, la inversión comienza a generar ganancias netas.
        </p>
      </div>
    ) : null
  };

  // Prepare visibility controls for DynamicChartSystem
  const visibilityControls = {
    cashflow: {
      visibility: cashFlowChartVisibility,
      toggle: toggleCashFlowChartElement
    }
  };

  // Prepare reset functions for DynamicChartSystem
  const resetVisibilityFunctions = {
    cashflow: resetCashFlowChartVisibility
  };

  const saveVisualization = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8">
          <title>Evolución del Flujo de Efectivo - Cosecha Capital</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              line-height: 1.5;
              margin: 0;
              padding: 20px;
              max-width: 1200px;
              margin: 0 auto;
            }
            h1 {
              color: #16a34a;
              margin-bottom: 20px;
            }
            p {
              color: #4b5563;
              margin-bottom: 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              padding: 12px;
              text-align: right;
              border-bottom: 1px solid #e5e7eb;
            }
            th:first-child, td:first-child {
              text-align: left;
            }
            th {
              background-color: #f0fdf4;
              color: #166534;
              font-weight: 600;
            }
            tr:nth-child(even) {
              background-color: #f9fafb;
            }
            .amber {
              color: #d97706;
            }
            .notes {
              background-color: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin-top: 30px;
            }
            .notes h3 {
              color: #374151;
              margin-bottom: 10px;
            }
            .notes ul {
              color: #4b5563;
              padding-left: 20px;
            }
          </style>
        </head>
        <body>
          <h1>Flujo de Efectivo Anual - Cosecha Capital</h1>
          <p>Análisis detallado del flujo de efectivo durante ${investment.years} años de inversión.</p>
          
          <table>
            <thead>
              <tr>
                <th>Año</th>
                ${cashFlowChartVisibility.cosechaCapital ? '<th>Flujo Cosecha Capital</th>' : ''}
                ${cashFlowChartVisibility.cetes ? '<th>Flujo CETES</th>' : ''}
                ${cashFlowChartVisibility.savings ? '<th>Flujo Ahorro</th>' : ''}
                ${cashFlowChartVisibility.realEstate ? '<th>Flujo Bienes Raíces</th>' : ''}
                ${cashFlowChartVisibility.cumulativeFlow && cashFlowChartVisibility.cosechaCapital ? '<th>Flujo Acumulado</th>' : ''}
                ${investment.partialCashOut && cashFlowChartVisibility.showPartialCashOut ? '<th>Retiro Parcial</th>' : ''}
              </tr>
            </thead>
            <tbody>
              ${yearlyData.map((data, index) => `
                <tr ${results.paybackYear && data.year === results.paybackYear ? 'class="highlight"' : ''}>
                  <td>${data.year}</td>
                  ${cashFlowChartVisibility.cosechaCapital ? 
                    `<td>
                      ${formatCurrency(data.availableCashFlow, investment.currencyFormat)}
                    </td>` : ''}
                  ${cashFlowChartVisibility.cetes ? 
                    `<td>
                      ${formatCurrency(data.cetesCashFlow || 0, investment.currencyFormat)}
                    </td>` : ''}
                  ${cashFlowChartVisibility.savings ? 
                    `<td>
                      ${formatCurrency(data.savingsCashFlow || 0, investment.currencyFormat)}
                    </td>` : ''}
                  ${cashFlowChartVisibility.realEstate ? 
                    `<td>
                      ${formatCurrency(data.realEstateCashFlow || 0, investment.currencyFormat)}
                    </td>` : ''}
                  ${cashFlowChartVisibility.cumulativeFlow && cashFlowChartVisibility.cosechaCapital ? 
                    `<td>
                      ${formatCurrency(data.availableCashFlow, investment.currencyFormat)}
                    </td>` : ''}
                  ${investment.partialCashOut && cashFlowChartVisibility.showPartialCashOut ? 
                    `<td class="amber">
                      ${formatCurrency(data.partialCashOutAmount || 0, investment.currencyFormat)}
                    </td>` : ''}
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="notes">
            <h3>Notas:</h3>
            <ul>
              <li>El flujo negativo inicial representa la inversión</li>
              <li>Los flujos positivos comienzan después del período de maduración</li>
              ${results.paybackYear ? 
                `<li>Recuperación de la inversión en el año ${results.paybackYear}</li>` :
                '<li>Período de recuperación superior al horizonte de inversión</li>'}
              <li>El flujo acumulado muestra el balance neto de la inversión en cada año</li>
              <li>Los valores no consideran el costo de oportunidad del dinero</li>
              ${investment.partialCashOut ? 
                `<li>Retiro parcial: ${investment.cashOutPercentage}% de las utilidades anuales</li>` : ''}
            </ul>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cosecha_capital_flujo_efectivo_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-green-800">Flujo de Efectivo Anual</h3>
        <div className="flex items-center gap-3">
          <button
            onClick={saveVisualization}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title="Guardar esta visualización como HTML"
          >
            <Save className="w-5 h-5" />
            <span>Guardar</span>
          </button>
          <button
            onClick={() => setShowTeachingModule(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            title="Abrir tutorial de esta sección"
          >
            <BookOpenIcon className="w-5 h-5" />
            <span>Tutorial</span>
          </button>
        </div>
      </div>

      {/* Dynamic Chart System */}
      <div className="flex-1 min-h-[420px]">
        <DynamicChartSystem
          categories={chartCategories}
          chartData={chartSystemData}
          chartElements={chartSystemElements}
          pieData={pieChartData}
          formatters={{
            yLabel: (value) => shortenLargeNumber(value),
            tooltip: (value) => formatCurrency(value, investment.currencyFormat)
          }}
          summaryData={summaryData}
          additionalInfo={additionalInfo}
          investment={investment}
          visibilityControls={visibilityControls}
          resetVisibilityFunctions={resetVisibilityFunctions}
          hideAdditionalOptions={true}
        />
      </div>
      
      {/* Teaching Module */}
      <TeachingModule
        title={teachingContent.cashflow.title}
        steps={teachingContent.cashflow.steps}
        isOpen={showTeachingModule}
        onClose={() => setShowTeachingModule(false)}
      />
    </div>
  );
};

export default CashFlowChart;